import { Button, Input, Label } from '@servicetim/ui/components';
import { Plus, Trash2 } from 'lucide-react';
import { type UseFormReturn, useFieldArray } from 'react-hook-form';
import { keyFromName, uniqueKey } from '../lib/list-utils';
import { ReorderButtons } from '../ui/reorder-buttons';
import type { SettingsFormValues } from './project-settings-form';

/** Editable list of portal categories (`key` + `label`) with reorder; issue types reference the key. */
export function PortalCategoriesEditor({ form }: { form: UseFormReturn<SettingsFormValues> }) {
  const { fields, append, remove, move } = useFieldArray({
    control: form.control,
    name: 'portalCategories',
  });
  const errors = form.formState.errors.portalCategories;
  const add = () => {
    const taken = new Set(form.getValues('portalCategories').map((c) => c.key));
    append({ key: uniqueKey('category', taken), label: 'New category' });
  };
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>Portal categories</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={add}
          disabled={fields.length >= 50}
        >
          <Plus />
          Add category
        </Button>
      </div>
      {fields.length === 0 ? (
        <p className="text-fg-muted text-sm">
          No categories: request types are listed flat in the portal.
        </p>
      ) : (
        <ul className="space-y-2">
          {fields.map((row, index) => {
            const rowErrors = errors?.[index];
            return (
              <li key={row.id} className="flex flex-wrap items-start gap-2">
                <div className="w-44 space-y-1">
                  <Input
                    aria-label={`Category ${index + 1} key`}
                    {...form.register(`portalCategories.${index}.key`)}
                    maxLength={40}
                    className="font-mono"
                    aria-invalid={rowErrors?.key ? true : undefined}
                  />
                  {rowErrors?.key ? (
                    <p role="alert" className="text-red-600 dark:text-red-400 text-xs">
                      {rowErrors.key.message}
                    </p>
                  ) : null}
                </div>
                <div className="min-w-48 flex-1 space-y-1">
                  <Input
                    aria-label={`Category ${index + 1} label`}
                    {...form.register(`portalCategories.${index}.label`, {
                      onBlur: (e) => {
                        const key = form.getValues(`portalCategories.${index}.key`);
                        if (key === '' || key.startsWith('category')) {
                          form.setValue(
                            `portalCategories.${index}.key`,
                            keyFromName(e.target.value) || key,
                            { shouldDirty: true },
                          );
                        }
                      },
                    })}
                    maxLength={80}
                    aria-invalid={rowErrors?.label ? true : undefined}
                  />
                  {rowErrors?.label ? (
                    <p role="alert" className="text-red-600 dark:text-red-400 text-xs">
                      {rowErrors.label.message}
                    </p>
                  ) : null}
                </div>
                <ReorderButtons
                  label={`category ${index + 1}`}
                  onUp={index > 0 ? () => move(index, index - 1) : undefined}
                  onDown={index < fields.length - 1 ? () => move(index, index + 1) : undefined}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove category ${index + 1}`}
                  onClick={() => remove(index)}
                >
                  <Trash2 />
                </Button>
              </li>
            );
          })}
        </ul>
      )}
      {errors?.root?.message || (typeof errors?.message === 'string' && errors.message) ? (
        <p role="alert" className="text-red-600 dark:text-red-400 text-xs">
          {errors.root?.message ?? errors.message}
        </p>
      ) : null}
    </div>
  );
}
